import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, BookOpen, Heart, Users, ArrowRight, Star, Sun } from "lucide-react";
import { getTodayLesson, listLessons } from "@/lib/api";
import LessonCard from "@/components/LessonCard";
import Loading from "@/components/Loading";
import SubscribeForm from "@/components/SubscribeForm";
import KindnessTicker from "@/components/KindnessTicker";

const HERO_IMG =
    "https://images.unsplash.com/photo-1469228252629-cbe7cb7db2c8?fm=jpg&q=70&w=1200&fit=crop&auto=format";
const HERO_IMG_FALLBACK =
    "https://images.unsplash.com/photo-1480561807109-e2aa33f23be8?fm=jpg&q=70&w=1200&fit=crop&auto=format";

export default function Home() {
    const [today, setToday] = useState(null);
    const [recent, setRecent] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let mounted = true;
        async function load() {
            try {
                setLoading(true);
                const t = await getTodayLesson();
                if (mounted) setToday(t);
                const list = await listLessons({ limit: 6 });
                if (mounted) setRecent(list.filter((l) => l.id !== t.id).slice(0, 6));
            } catch (e) {
                console.error("Failed to load home", e);
            } finally {
                if (mounted) setLoading(false);
            }
        }
        load();
        return () => { mounted = false; };
    }, []);

    return (
        <div data-testid="home-page">
            {/* HERO */}
            <section className="relative overflow-hidden border-b-2 border-ink">
                <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14 lg:py-20 grid lg:grid-cols-2 gap-12 items-center">
                    <div className="relative z-10">
                        <span className="pill bg-mint mb-5" data-testid="hero-eyebrow">
                            <Sun className="w-3.5 h-3.5" strokeWidth={2.5} /> A new lesson every day
                        </span>
                        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
                            Big stories. <br />
                            Little hearts. <br />
                            <span className="bg-sun px-3 inline-block border-2 border-ink rounded-2xl shadow-sticker-sm rotate-[-2deg] mt-2">
                                Real-world good.
                            </span>
                        </h1>
                        <p className="mt-6 text-lg text-ink/80 max-w-xl leading-relaxed">
                            Daily Christian Bible lessons made just for kids — drawn from God's Word (ESV), with simple stories, age-by-age
                            explanations, and Christ-like ways to <em className="font-accent text-2xl text-coral not-italic">go the second mile</em> in your neighborhood today.
                        </p>

                        <div className="mt-8 flex flex-wrap gap-4">
                            <Link to="/lessons" className="btn-sticker bg-sun text-ink" data-testid="hero-cta-primary">
                                <BookOpen className="w-5 h-5 mr-2" strokeWidth={2.5} />
                                Read Today's Lesson
                            </Link>
                            <Link to="/community" className="btn-sticker bg-white text-ink" data-testid="hero-cta-secondary">
                                <Users className="w-5 h-5 mr-2" strokeWidth={2.5} />
                                Community Hub
                            </Link>
                        </div>

                        <div className="mt-10 flex items-center gap-6 text-sm">
                            <div className="flex items-center gap-2">
                                <span className="w-3 h-3 rounded-full bg-mint border-2 border-ink" />
                                Ages 5–8
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="w-3 h-3 rounded-full bg-coral border-2 border-ink" />
                                Ages 9–12
                            </div>
                        </div>
                    </div>

                    <div className="relative">
                        <div className="absolute -top-6 -left-6 w-20 h-20 bg-coral border-2 border-ink rounded-full shadow-sticker animate-float flex items-center justify-center font-accent text-2xl text-white rotate-[-8deg] z-20">
                            love!
                        </div>
                        <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-sun border-2 border-ink rounded-3xl shadow-sticker animate-float flex items-center justify-center z-20" style={{ animationDelay: "1s" }}>
                            <Star className="w-12 h-12 text-ink" strokeWidth={2.5} />
                        </div>
                        <div className="rounded-[2rem] border-2 border-ink overflow-hidden shadow-sticker-lg bg-white relative">
                            <img
                                src={HERO_IMG}
                                onError={(e) => { if (e.target.src !== HERO_IMG_FALLBACK) e.target.src = HERO_IMG_FALLBACK; }}
                                alt="An open Holy Bible resting in the sunlight"
                                className="w-full h-[420px] object-cover"
                            />
                        </div>
                    </div>
                </div>

                {/* Decorative dots */}
                <div className="absolute top-12 right-4 w-6 h-6 rounded-full bg-mint border-2 border-ink hidden lg:block" />
                <div className="absolute bottom-20 left-12 w-4 h-4 rounded-full bg-coral border-2 border-ink hidden lg:block" />
            </section>

            {/* TODAY'S LESSON */}
            <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
                <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
                    <div>
                        <span className="pill bg-coral text-white mb-3">
                            <Sparkles className="w-3.5 h-3.5" strokeWidth={2.5} /> Today
                        </span>
                        <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">
                            Today's Bible Lesson
                        </h2>
                    </div>
                    <Link to="/lessons" className="font-heading font-semibold inline-flex items-center gap-1 hover:underline" data-testid="see-all-lessons">
                        See all lessons <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
                    </Link>
                </div>

                {loading && !today && <Loading />}
                {today && (
                    <div className="grid lg:grid-cols-3 gap-8">
                        <div className="lg:col-span-2">
                            <LessonCard lesson={today} featured />
                        </div>

                        <div className="card-sticker p-6 bg-sun" data-testid="key-verse-card">
                            <p className="font-accent text-2xl text-ink/80">key verse</p>
                            <p className="font-heading text-xl font-bold mt-1">{today.key_verse}</p>
                            <blockquote className="mt-4 text-lg leading-relaxed border-l-4 border-ink pl-4 italic">
                                "{today.key_verse_text}"
                            </blockquote>
                            <div className="mt-6">
                                <p className="font-accent text-2xl text-ink/80">a tiny prayer</p>
                                <p className="mt-2 leading-relaxed">{today.prayer}</p>
                            </div>
                        </div>
                    </div>
                )}
            </section>

            {/* COMMUNITY KINDNESS TICKER */}
            <KindnessTicker />

            {/* HOW IT WORKS */}
            <section className="bg-white border-y-2 border-ink py-16">
                <div className="max-w-7xl mx-auto px-5 sm:px-8">
                    <div className="text-center max-w-2xl mx-auto mb-12">
                        <span className="pill bg-lilac mb-3">How it works</span>
                        <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">
                            From the Bible to your block
                        </h2>
                        <p className="mt-3 text-ink/70 text-lg">
                            Every lesson takes kids on a 3-step journey from a story to real action.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6">
                        {[
                            { icon: BookOpen, color: "bg-sun", title: "1. Read the story", desc: "A warm, kid-friendly retelling with a key Bible verse." },
                            { icon: Heart, color: "bg-coral", title: "2. Talk about it", desc: "Two age-appropriate explanations + reflection questions." },
                            { icon: Users, color: "bg-mint", title: "3. Live it out", desc: "Three ideas for serving your local community this week." },
                        ].map((s, i) => (
                            <div key={i} className="card-sticker p-7" data-testid={`how-step-${i + 1}`}>
                                <div className={`inline-flex items-center justify-center w-14 h-14 rounded-2xl border-2 border-ink ${s.color} mb-4`}>
                                    <s.icon className="w-7 h-7 text-ink" strokeWidth={2.5} />
                                </div>
                                <h3 className="font-heading text-xl font-bold mb-2">{s.title}</h3>
                                <p className="text-ink/70 leading-relaxed">{s.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* SUBSCRIBE BAND */}
            <section className="bg-sky/30 border-y-2 border-ink py-14">
                <div className="max-w-3xl mx-auto px-5 sm:px-8">
                    <SubscribeForm variant="hero" />
                </div>
            </section>

            {/* RECENT */}
            <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
                <div className="flex items-end justify-between mb-8">
                    <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">
                        Lately on 2nd Mile Kids
                    </h2>
                    <Link to="/lessons" className="font-heading font-semibold hidden sm:inline-flex items-center gap-1 hover:underline">
                        Browse archive <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
                    </Link>
                </div>
                {loading && recent.length === 0 ? (
                    <Loading label="Gathering lessons..." />
                ) : recent.length === 0 ? (
                    <div className="card-sticker p-8 text-center bg-white" data-testid="empty-recent">
                        <p className="font-heading text-xl">More lessons are on the way!</p>
                        <p className="text-ink/70 mt-2">Come back tomorrow for a fresh story.</p>
                    </div>
                ) : (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {recent.map((l) => (
                            <LessonCard key={l.id} lesson={l} />
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
}
